import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2, Bell, Mail, MessageSquare, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import heartLoading from '../../../../assests/heartLoading.gif';

const SLAandAlerts = ({ publishData, onDataRefresh, showProductSLA = true }) => {
  const [receiveAlerts, setReceiveAlerts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [receiveTeamsAlerts, setReceiveTeamsAlerts] = useState(false);
  const [receiveEmailAlerts, setReceiveEmailAlerts] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [emails, setEmails] = useState(['']);
  const [emailErrors, setEmailErrors] = useState(['']);
  const [alertType, setAlertType] = useState("");
  const [editMode, setEditMode] = useState({});
  const [alertTeamConfig, setAlertTeamConfig] = useState({
    channel_name: '',
    webhook_link: '',
  });
  const [alertEmailConfig, setAlertEmailConfig] = useState({
    email: '',
    alert_url: process.env.REACT_APP_EMAIL_ALERT_URL
  });

  const [formData, setFormData] = useState({
    product_id: publishData?.ProductInfo?.productId,
    alert_type: '',
    alert_config: '',
    is_active: true,
    created_by: localStorage.getItem('userName') || 'user',
    last_modified_by: localStorage.getItem('userName') || 'user',
  });

  useEffect(() => {
    const alertType = receiveTeamsAlerts ? "TEAMS" : "EMAIL";
    const alertConfig = alertType === "TEAMS" ? alertTeamConfig : alertEmailConfig;
    setAlertType(alertType);
    setFormData(prev => ({ ...prev, alert_type: alertType, alert_config: JSON.stringify(alertConfig) }));
  }, [receiveEmailAlerts, receiveTeamsAlerts, alertTeamConfig, alertEmailConfig, emails]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@effem\.com$/;
    return emailRegex.test(email);
  };

  // Get all existing emails from configured alerts
  const getExistingEmails = () => {
    const emailAlerts = publishData?.SLAandAlerts?.Alerts?.filter(a => a.AlertType === 'Email') || [];
    return emailAlerts.flatMap(alert => alert.Emails || []).map(e => e.toLowerCase());
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    const newErrors = [...emailErrors];
    newEmails[index] = value;
    
    if (value === '') {
      newErrors[index] = '';
    } else if (!validateEmail(value)) {
      newErrors[index] = 'Only @effem.com email addresses are allowed';
    } else {
      // Check if email already exists in configured alerts
      const existingEmails = getExistingEmails();
      if (existingEmails.includes(value.toLowerCase())) {
        newErrors[index] = 'This email already exists, add different email';
      } else {
        // Check for duplicates within the current form
        const duplicateInForm = newEmails.filter((e, i) => i !== index && e.toLowerCase() === value.toLowerCase()).length > 0;
        if (duplicateInForm) {
          newErrors[index] = 'This email already exists, add different email';
        } else {
          newErrors[index] = '';
        }
      }
    }
    
    setEmails(newEmails);
    alertEmailConfig.email = newEmails.join(', ');
    setEmailErrors(newErrors);
  };

  const addEmail = () => {
    // No limit on adding emails
    setEmails([...emails, '']);
    setEmailErrors([...emailErrors, '']);
  };

  const removeEmail = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      const newErrors = emailErrors.filter((_, i) => i !== index);
      setEmails(newEmails);
      setEmailErrors(newErrors);
    }
  };

  const setTeamsMode = () => {
    setReceiveTeamsAlerts(true);
    setReceiveEmailAlerts(false);
  };

  const setEmailMode = () => {
    setReceiveTeamsAlerts(false);
    setReceiveEmailAlerts(true);
  };

  const handleInputChange = (field, value) => {
    setAlertTeamConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const alertConfig = receiveTeamsAlerts ? alertTeamConfig : alertEmailConfig;
      
      if (receiveEmailAlerts) {
        // Get ALL existing email alerts
        const existingEmailAlerts = publishData?.SLAandAlerts?.Alerts?.filter(
          alert => alert.AlertType === 'Email'
        ) || [];

        if (existingEmailAlerts.length > 0) {
          // Collect all emails from all existing email alerts
          const allExistingEmails = existingEmailAlerts.flatMap(alert => alert.Emails || []);
          const newEmails = emails.filter(email => email.trim() !== '');
          const combinedEmails = [...new Set([...allExistingEmails, ...newEmails])];
          
          // Update the first email alert with all combined emails
          const firstAlert = existingEmailAlerts[0];
          const updatePayload = {
            alert_config: JSON.stringify({
              email: combinedEmails.join(', '),
              alert_url: process.env.REACT_APP_EMAIL_ALERT_URL
            }),
            last_modified_by: localStorage.getItem('userName') || 'user',
          };
          
          await axios.put(
            `${process.env.REACT_APP_API_URL}/alerts/update/${firstAlert.AlertId}`,
            updatePayload
          );

          // Delete all other email alerts (merge into one)
          for (let i = 1; i < existingEmailAlerts.length; i++) {
            await axios.delete(`${process.env.REACT_APP_API_URL}/alerts/${existingEmailAlerts[i].AlertId}`);
          }
        } else {
          // Create new email alert
          const payload = {
            product_id: publishData?.ProductInfo?.productId,
            alert_type: "EMAIL",
            alert_config: JSON.stringify({
              email: emails.join(', '),
              alert_url: process.env.REACT_APP_EMAIL_ALERT_URL
            }),
            is_active: true,
            created_by: localStorage.getItem('userName') || 'user',
            last_modified_by: localStorage.getItem('userName') || 'user',
          };
          
          await axios.post(
            `${process.env.REACT_APP_API_URL}/alerts/create/${publishData?.ProductInfo?.productId}`,
            payload
          );
        }
      } else {
        // Teams alert
        const payload = {
          product_id: publishData?.ProductInfo?.productId,
          alert_type: "TEAMS",
          alert_config: JSON.stringify(alertConfig),
          is_active: true,
          created_by: localStorage.getItem('userName') || 'user',
          last_modified_by: localStorage.getItem('userName') || 'user',
        };
        
        await axios.post(
          `${process.env.REACT_APP_API_URL}/alerts/create/${publishData?.ProductInfo?.productId}`,
          payload
        );
      }

      toast.success('Alert configuration submitted.');
      setReceiveAlerts(false);
      setReceiveEmailAlerts(false);
      setReceiveTeamsAlerts(false);
      setEmails(['']);
      setEmailErrors(['']);
      setAlertTeamConfig({ channel_name: "", webhook_link: "" });
      
      await fetchLatestPublishData();
    } catch (error) {
      toast.error('Failed to submit alert');
    } finally {
      setLoading(false);
    }
  };

  const fetchLatestPublishData = async () => {
    if (!publishData?.ProductInfo?.productId) return;
    setFetchLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/product-overview-v2/${publishData.ProductInfo.productId}`);
      if (onDataRefresh) {
        onDataRefresh(response.data);
      }
    } catch (error) {
      console.error('Error fetching latest publish data:', error);
      toast.error('Failed to refresh alert data');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleDeleteClick = (alertId) => {
    setAlertToDelete(alertId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!alertToDelete) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/alerts/${alertToDelete}`);
      toast.success('Alert deleted successfully');
      setDeleteConfirmOpen(false);
      setAlertToDelete(null);
      await fetchLatestPublishData();
    } catch (error) {
      toast.error('Failed to delete alert');
      console.error('Error deleting alert:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAlertToDelete(null);
  };

  const hasTeamsAlert = publishData?.SLAandAlerts?.Alerts?.some(alert => alert.AlertType === "Teams");
  const hasEmailAlert = publishData?.SLAandAlerts?.Alerts?.some(alert => alert.AlertType === "Email");
  const hasNoAlerts = !publishData?.SLAandAlerts?.Alerts || publishData.SLAandAlerts.Alerts.length === 0;

  const toggleEditMode = (alertId) => {
    setEditMode(prev => ({
      ...prev,
      [alertId]: !prev[alertId]
    }));
  };

  const handleDeleteEmailFromAlert = async (alertId, emailToRemove, allEmails) => {
    try {
      setDeleteLoading(true);
      const updatedEmails = allEmails.filter(email => email !== emailToRemove);

      if (updatedEmails.length === 0) {
        await axios.delete(`${process.env.REACT_APP_API_URL}/alerts/${alertId}`);
        toast.success('Alert deleted successfully (no emails remaining)');
      } else {
        const updatePayload = {
          alert_config: JSON.stringify({
            email: updatedEmails.join(', '),
            alert_url: process.env.REACT_APP_EMAIL_ALERT_URL
          }),
          last_modified_by: localStorage.getItem('userName') || 'user',
        };
        
        await axios.put(`${process.env.REACT_APP_API_URL}/alerts/update/${alertId}`, updatePayload);
        toast.success('Email removed successfully');
      }

      await fetchLatestPublishData();
      setEditMode(prev => ({ ...prev, [alertId]: false }));
    } catch (error) {
      toast.error('Failed to remove email');
      console.error('Error removing email:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const isAddButtonDisabled = () => {
    return emails.some(email => email === '' || !validateEmail(email)) || emailErrors.some(e => e !== '');
  };

  const isSubmitButtonDisabled = () => {
    if (alertType === "TEAMS")
      return alertTeamConfig.channel_name === '' || alertTeamConfig.webhook_link === '';
    // Check for empty emails, invalid emails, or any errors (including duplicates)
    return emails.some(email => email === '' || !validateEmail(email)) || emailErrors.some(e => e !== '');
  };
  const isLoading = loading || deleteLoading || fetchLoading;
  const getLoadingMessage = () => {
    if (loading) return 'Submitting alert...';
    if (deleteLoading) return 'Deleting alert...';
    if (fetchLoading) return 'Refreshing data...';
    return 'Loading...';
  };

  const slaAndAlerts = publishData?.SLAandAlerts || {};

  return (
    <div className="w-full">
      {/* Full Page Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center space-y-4 shadow-2xl">
            <img src={heartLoading} alt="Loading..." className="h-32 w-32" />
            <div className="text-center">
              <p className="text-gray-700 text-xl font-semibold">{getLoadingMessage()}</p>
              <p className="text-gray-500 text-base mt-2">Please wait while we process your request</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Notification Settings</h2>
        
        <div className="space-y-6 pl-2">
          {/* SLA Settings */}
          {showProductSLA && (
            <>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500 mb-2">SLA Response Time</p>
                  <p className="text-base text-gray-900">
                    {slaAndAlerts.slaResponseTime ? `${slaAndAlerts.slaResponseTime} hours` : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">SLA Uptime</p>
                  <p className="text-base text-gray-900">
                    {slaAndAlerts.slaUptime ? `${slaAndAlerts.slaUptime}%` : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 "></div>
            </>
          )}

          {/* No alerts message */}
          {hasNoAlerts && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">No alerts configured yet. Configure alerts below to receive notifications.</p>
            </div>
          )}

          {/* Existing Alerts Section */}
          {!hasNoAlerts && (
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Configured Alerts</h3>
              
              {/* Teams Alerts */}
              {slaAndAlerts.Alerts && slaAndAlerts.Alerts.filter(a => a.AlertType === 'Teams').map((alert, index) => (
                <div key={alert.AlertId || index} className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-purple-600" />
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Teams
                      </span>
                    </div>
                    <button
                      onClick={() => handleDeleteClick(alert?.AlertId)}
                      className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                      disabled={isLoading}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Channel Name</p>
                      <p className="text-base text-gray-900">{alert.ChannelName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Webhook Link</p>
                      <a 
                        href={alert.WebhookLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-base text-blue-600 hover:underline truncate block"
                      >
                        {alert.WebhookLink || 'N/A'}
                      </a>
                    </div>
                  </div>
                </div>
              ))}

              {/* Email Alerts - Combined into single card */}
              {slaAndAlerts.Alerts && slaAndAlerts.Alerts.some(a => a.AlertType === 'Email') && (() => {
                const emailAlerts = slaAndAlerts.Alerts.filter(a => a.AlertType === 'Email');
                const allEmails = emailAlerts.flatMap(alert => 
                  (alert.Emails || []).map(email => ({ email, alertId: alert.AlertId }))
                );
                const firstAlertId = emailAlerts[0]?.AlertId;
                
                return (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Email
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleEditMode('email-alerts')}
                          className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                          disabled={isLoading}
                        >
                          <Edit2 size={14} />
                          {editMode['email-alerts'] ? 'Done' : 'Edit'}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(firstAlertId)}
                          className="flex items-center gap-1 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          disabled={isLoading}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Email Recipients</p>
                      <div className="flex flex-wrap gap-2">
                        {allEmails.length > 0 
                          ? allEmails.map((item, emailIndex) => (
                              <span 
                                key={emailIndex}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {item.email}
                                {editMode['email-alerts'] && (
                                  <button
                                    onClick={() => handleDeleteEmailFromAlert(item.alertId, item.email, 
                                      emailAlerts.find(a => a.AlertId === item.alertId)?.Emails || []
                                    )}
                                    className="ml-1.5 text-red-600 hover:text-red-800 transition-colors"
                                    title="Remove this email"
                                    disabled={isLoading}
                                  >
                                    <X size={14} />
                                  </button>
                                )}
                              </span>
                            ))
                          : <span className="text-gray-500">No emails configured</span>
                        }
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Add New Alert Section */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Alert</h3>
            
            <div className="space-y-4">
              {/* Receive Alerts Toggle */}
              <div>
                <p className="text-sm text-gray-500 mb-2">Would you like to receive DQ alerts?</p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="receiveAlerts"
                      checked={receiveAlerts === true}
                      onChange={() => setReceiveAlerts(true)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="receiveAlerts"
                      checked={receiveAlerts === false}
                      onChange={() => setReceiveAlerts(false)}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      disabled={isLoading}
                    />
                    <span className="text-sm text-gray-700">No</span>
                  </label>
                </div>
              </div>

              {/* Alert Type Selection */}
              {receiveAlerts && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Select Alert Type</p>
                  <div className="flex items-center gap-4">
                    <Tooltip
                      title={hasTeamsAlert ? "You already have a Teams alert. Remove the existing one to add a new one." : ""}
                      arrow
                    >
                      <label className={`flex items-center gap-2 ${hasTeamsAlert ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="alertType"
                          checked={receiveTeamsAlerts}
                          onChange={() => setTeamsMode()}
                          disabled={hasTeamsAlert || isLoading}
                          className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <MessageSquare className=" h-4 text-purple-600" />
                        <span className="text-sm text-gray-700">Teams</span>
                      </label>
                    </Tooltip>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="alertType"
                        checked={receiveEmailAlerts}
                        onChange={() => setEmailMode()}
                        disabled={isLoading}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <Mail className="h-4 text-blue-600" />
                      <span className="text-sm text-gray-700">Email</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Teams Configuration */}
              {receiveAlerts && receiveTeamsAlerts && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4" />
                    Teams Configuration
                  </h4>
                  <div className="grid grid-cols-2 gap-4 pl-2">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Channel Name</p>
                      <input
                        type="text"
                        value={alertTeamConfig.channel_name}
                        onChange={e => handleInputChange('channel_name', e.target.value)}
                        placeholder="Enter channel name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        disabled={isLoading}
                      />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Webhook Link</p>
                      <input
                        type="text"
                        value={alertTeamConfig.webhook_link}
                        onChange={e => handleInputChange('webhook_link', e.target.value)}
                        placeholder="Enter webhook URL"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Email Configuration */}
              {receiveAlerts && receiveEmailAlerts && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                    <Mail className="h-4" />
                    Email Configuration
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">Enter email addresses (only @effem.com addresses allowed)</p>
                  
                  <div className="space-y-2">
                    {emails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          placeholder="Enter @effem.com email"
                          className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${
                            emailErrors[index] ? 'border-red-500' : 'border-gray-300'
                          }`}
                          disabled={isLoading}
                        />
                        {emails.length > 1 && (
                          <button
                            onClick={() => removeEmail(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            disabled={isLoading}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    {emailErrors.some(e => e) && (
                      <p className="text-red-500 text-xs">{emailErrors.find(e => e)}</p>
                    )}
                  </div>
                  
                  <button
                    onClick={addEmail}
                    disabled={isAddButtonDisabled() || isLoading}
                    className={`mt-3 flex items-center gap-1 text-sm ${
                      isAddButtonDisabled() || isLoading
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-800'
                    }`}
                  >
                    <Plus size={14} />
                    Add another email
                  </button>
                </div>
              )}

              {/* Submit Button */}
              {receiveAlerts && (receiveTeamsAlerts || receiveEmailAlerts) && (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitButtonDisabled() || isLoading}
                  className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSubmitButtonDisabled() || isLoading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#FF03F3] text-white hover:bg-[#FF03F3]'
                  }`}
                >
                  Add Alert
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.25rem', color: '#1f2937' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <p className="text-gray-700 text-base">
            Are you sure you want to delete this alert? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={isLoading}
            sx={{
              color: '#6b7280',
              borderColor: '#d1d5db',
              '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export { SLAandAlerts };
